export enum EducateType {
  CONCEPT_WALKTHROUGH = 'conceptWalkthrough',
  SCENARIO_LED = 'scenarioLed',
  DECISION_TREE = 'decisionTree',
  CASE_STUDY = 'caseStudy',
  DIAGNOSTIC = 'diagnostic',
  AUDIOBOOK = 'audioBook',
  PODCAST = 'podcast'
}

export const EDUCATE_TYPES = Object.values(EducateType);

export enum ProductGroup {
  AI_MICRO_LEARN = 'aiMicroLearn',
  INTERACTIVE_LEARNING = 'interactiveLearning'
}

export interface Flashcard {
  term: string;
  definition: string;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  feedback?: string;
}

export interface CastMember {
  name: string;
  role: string;
}

export interface ConceptWalkthroughContent {
  title: string;
  sections: Array<{
    title: string;
    paragraphs: string[];
    flashcards?: Flashcard[];
    quiz?: QuizQuestion[];
  }>;
}

export interface ScenarioLedContent {
  title: string;
  setting?: string;
  cast?: CastMember[];
  situation: string;
  teachingBeats: Array<{ point: string; explanation: string }>;
  resolution: string;
  check?: QuizQuestion;
}

export interface DecisionTreeContent {
  title: string;
  setting?: string;
  cast?: CastMember[];
  startNodeId: string;
  nodes: DecisionTreeNode[];
}

export interface CaseStudyContent {
  title: string;
  setting?: string;
  cast?: CastMember[];
  stamps?: string[];
  brief?: string;
  pages: Array<{ title?: string; narrative: string; analysis: string; modelAnswer: string }>;
  synthesis?: string;
}

export interface DecisionTreeNode {
  id: string;
  title?: string;
  situation: string;
  choices?: Array<{
    label: string;
    consequence: string;
    nextNodeId: string | null;
  }>;
  terminal?: boolean;
  outcome?: string;
}

export interface DiagnosticContent {
  title: string;
  systemDescription: string;
  symptoms: string[];
  hypotheses: Array<{
    cause: string;
    test: string;
    isRootCause?: boolean;
    reasoning: string;
  }>;
  resolution: string;
  check?: QuizQuestion;
}

export interface VoiceAssignment {
  provider: string;
  voiceId: string;
  voiceRefId: string;
  voiceName: string;
  gender: string;
}

export interface TextUnit {
  id: string;
  sequenceNo: number;
  speaker: 'narrator' | 'host' | 'expert';
  gender: string;
  provider: string;
  voiceId: string;
  voiceRefId: string;
  voiceName: string;
  textContent: string;
  contentHash: string;
  audioUrl: string | null;
  status: string;
}

export interface AudiobookContent {
  title: string;
  description?: string;
  narrator?: { gender: string };
  slides: Array<{ title: string; bullets: string[]; script: string; audioUrl?: string }>;
  voiceAssignments?: Record<string, VoiceAssignment>;
  textUnits?: TextUnit[];
}

export interface PodcastContent {
  title: string;
  description?: string;
  cast?: { host: { gender: string }; expert: { gender: string } };
  turns: Array<{ speaker: 'host' | 'expert'; text: string; ssml?: string; audioUrl?: string }>;
  voiceAssignments?: Record<string, VoiceAssignment>;
  textUnits?: TextUnit[];
}

export type EducateContent =
  | ConceptWalkthroughContent
  | ScenarioLedContent
  | DecisionTreeContent
  | CaseStudyContent
  | DiagnosticContent
  | AudiobookContent
  | PodcastContent;

export interface EducateStoryline {
  storylineId: string;
  productType: ProductGroup;
  fourEProductType?: string;
  educateType: EducateType;
  productLine?: EducateType;
  uiContent: {
    title: string;
    description: string;
    objectives: string[];
    uiConfig?: {
      showKnolskapeLogo?: boolean;
      customBranding?: { logoUrl?: string; btnColor?: string; btnTextColor?: string };
    };
    timerConfig?: {
      timeLimit: number;
      showTimer?: boolean;
      autoEndOnTimeout?: boolean;
    };
  };
  content: EducateContent;
  totalUnits: number;
  completionConfig: {
    requireAllUnits: boolean;
    passPercent: number;
    scoreFromQuiz: boolean;
  };
  voiceConfig: {
    provider: 'elevenlabs' | 'deepgram' | 'gemini' | 'browser';
    voiceId?: string;
    hostVoiceId?: string;
    expertVoiceId?: string;
  };
}

export type ProgressStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface EducateInteraction {
  unitId: string;
  kind: 'quiz' | 'choice' | 'reflection' | 'view';
  answer?: unknown;
  isCorrect?: boolean | null;
  score?: number | null;
}

export interface EducateProgress {
  status: ProgressStatus;
  progress: {
    currentIndex: number;
    currentUnitId?: string | null;
    totalUnits: number;
    completedUnits: string[];
    percentComplete: number;
  };
  interactions: EducateInteraction[];
  score: { correct: number; total: number; percent: number; passed: boolean };
  timeSpentSeconds: number;
}

export interface EducateBootstrap {
  storyline: EducateStoryline;
  progress: EducateProgress;
}
